import React from 'react';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import Modal, { ModalSize } from '@ssg/common/Components/Modal';
import Button from '@ssg/common/Components/Button';
import {
	GetDebitorDelayScalepointConnectionsDocument,
	GetDebitorDelayScalepointConnectionsQuery,
	GetDebitorDelayScalepointConnectionsQueryVariables,
	useEditDebitorDelayScalepointConnectionMutation,
	useGetAdminDamageCausesQuery,
} from '@ssg/common/GraphQL/indexV2';
import * as yup from 'yup';
import SearchableSelect from '@ssg/common/Components/SearchableSelect';
import { getDebitorName } from './DebitorScalepointSetup';
import { SelectOption } from '@ssg/common/Helpers/Helpers';
import TextButton from '@ssg/common/Components/TextButton';
import { faTimes } from '@fortawesome/pro-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import Input from '@ssg/common/Components/Input';

export const EditDebitorDelayScalepointConnectionSchema = yup.object().shape({
	id: yup.string().required(),
});
interface Props {
	close: () => unknown;
	debitorScalepointConnection: GetDebitorDelayScalepointConnectionsQuery['debitorDelayScalepointConnections'][0];
}

const DebitorScalepointEdit: React.FC<Props> = ({ close, debitorScalepointConnection }) => {
	const { t } = useTranslation();

	const { data: allDamageCausesData } = useGetAdminDamageCausesQuery();

	const [selectedDamageCauses, setSelectedDamageCauses] = React.useState<SelectOption[]>(
		debitorScalepointConnection.damageCauses.map(dc => ({ value: dc.id, label: `${dc.category.name} - ${dc.name}` })),
	);
	const [damageCauseSearchText, setDamageCauseSearchText] = React.useState<string>('');
	const searchedDamageCauses = React.useMemo(() => {
		return (
			allDamageCausesData?.damageCausesAdmin.map(dc => ({
				value: dc.id,
				label: `${dc.category.name} - ${dc.name}`,
			})) ?? []
		)
			.filter(dc => damageCauseSearchText.length === 0 || dc.label.toLowerCase().includes(damageCauseSearchText.toLowerCase()))
			.sort((a, b) => a.label.localeCompare(b.label));
	}, [allDamageCausesData, damageCauseSearchText]);

	const handleDamageCauseChange = (value: string): void => {
		if (value === '') return;
		setValue('damageCauseIds', [...selectedDamageCauses.map(dc => dc.value), value]);
		setSelectedDamageCauses([...selectedDamageCauses, { value, label: searchedDamageCauses.find(dc => dc.value === value)?.label ?? '' }]);
	};

	const [editDebitorScalepointConnection, { loading }] = useEditDebitorDelayScalepointConnectionMutation();

	const { handleSubmit, formState, control, setValue, getValues, register } = useForm<{
		id: string;
		damageCauseIds: string[];
	}>({
		resolver: yupResolver(EditDebitorDelayScalepointConnectionSchema),
		mode: 'all',
		defaultValues: {
			id: debitorScalepointConnection.id,
			damageCauseIds: debitorScalepointConnection.damageCauses.map(dc => dc.id),
		},
	});
	React.useEffect(() => {
		register('id');
		register('damageCauseIds');
	}, [register]);

	const onSubmit = async ({ id, damageCauseIds }: { id: string; damageCauseIds: string[] }) => {
		await editDebitorScalepointConnection({
			variables: {
				id,
				damageCauseIds,
			},
			update: (cache, { data }): void => {
				if (typeof data === 'undefined' || data === null) {
					return;
				}

				const cachedRequest = cache.readQuery<GetDebitorDelayScalepointConnectionsQuery, GetDebitorDelayScalepointConnectionsQueryVariables>({
					query: GetDebitorDelayScalepointConnectionsDocument,
				});
				if (cachedRequest === null || cachedRequest.debitorDelayScalepointConnections === null) {
					return;
				}

				cache.writeQuery<GetDebitorDelayScalepointConnectionsQuery>({
					query: GetDebitorDelayScalepointConnectionsDocument,
					data: {
						debitorDelayScalepointConnections: cachedRequest.debitorDelayScalepointConnections.map(dds =>
							dds.id === id ? { ...dds, damageCauses: data.editDebitorDelayScalepointConnection.damageCauses } : dds,
						),
					},
				});
			},
		});
		close();
	};

	return (
		<Modal
			size={ModalSize.SMALL}
			title="common.edit"
			visible
			close={close}
			body={
				<form onSubmit={handleSubmit(onSubmit)}>
					<div style={{ minHeight: 400 }} className="flex flex-col justify-between">
						<div>
							<Input name={'gfdgdfg '} value={getDebitorName(debitorScalepointConnection.debitor)} title="debitor.overviewTitle" readOnly />

							<SearchableSelect
								control={control}
								name="damageCauseSelecter"
								title="questionnaire.damageCauses"
								options={searchedDamageCauses.filter(u => !selectedDamageCauses.some(s => s.value === u.value))}
								searchFn={searchText => setDamageCauseSearchText(searchText)}
								onSelect={value => handleDamageCauseChange(value)}
								onBlur={() => undefined}
								minInputLength={-1}
								isLoading={false}
							/>
							{selectedDamageCauses.length === 0 && <p className="font-bold ml-1 mt-1">{t('debitor.allSelected')}</p>}

							<ul className="w-full space-y-1 py-1" style={{ maxHeight: 300, overflowY: 'auto' }}>
								{selectedDamageCauses.map(damageCause => (
									<li key={damageCause.value} className="flex items-center">
										<div className="inline-block">
											<TextButton
												icon={faTimes}
												onClick={() => {
													setSelectedDamageCauses(selectedDamageCauses.filter(u => u.value !== damageCause.value));
													setValue(
														'damageCauseIds',
														selectedDamageCauses.filter(u => u.value !== damageCause.value).map(dc => dc.value),
													);
												}}
											/>
										</div>
										<span className="ml-1">{damageCause.label}</span>
									</li>
								))}
							</ul>
						</div>
						<Button success submit text="common.save" loading={loading} />
					</div>
				</form>
			}
		/>
	);
};
export default DebitorScalepointEdit;

